import React, { useContext, useEffect, useState } from 'react'
import { useHistory } from 'react-router'

import { AcornsIcon } from '@acorns/icons'
import { useRemoteStrings } from '@acorns/web-utils'
import { useMutation } from '@apollo/react-hooks'
import { useFeature } from '@optimizely/react-sdk'
import PinwheelModal from '@pinwheel/react-modal'
import { compose } from 'recompose'
import styled from 'styled-components'

import CenteredSpinner from 'components/CenteredSpinner'
import Layout from 'components/Layout'
import Nav from 'components/Nav'
import { ActionType, PageName } from 'generated/types'
import { TestIDs } from 'test/utils/testIds'
import themeObj from 'theme'
import { WithFlowRouterProps, withFlowRouter } from 'utils/FlowProvider'
import { FlowRoute } from 'utils/FlowProvider/types'
import { FunnelContext } from 'utils/FunnelContext'
import { mocDDWaiver } from 'utils/Optimizely/features'

import { DisclosureDrawer } from './DisclosureDrawer'
import { useCheckingFundingTracking } from './events'
import FundingSourceImage from './funding-source.svg'
import { FundingMenu } from './FundingMenu'
import CreateDDLinkInfo from './gql/direct-deposit-link-mutation.gql'
import { Container, Content, StyledFootnote, StyledImage, Text, Title } from './styled-components'
import { Toast, showToast } from './Toast'
import { useCheckingFundingData } from './useCheckingFundingData'
import { useFundingInitiatedPolling, useKYCFailRedirect } from './useFundingInitiatedPolling'

type CheckingFundingProps = WithFlowRouterProps

const enhance = compose<CheckingFundingProps, {}>(withFlowRouter)

const Link = styled.a``

const CheckingFunding = enhance(({ route }) => {
  const { getRemoteString, StyledRemoteString } = useRemoteStrings()
  const history = useHistory()
  const track = useCheckingFundingTracking()
  const [showPinwheelModal, setShowPinwheelModal] = useState(false)
  const { isBackEnabled, isDynamicFunnelEnabled, routeToPage } = useContext(FunnelContext)
  const [pinwheelToken, setPinwheelToken] = useState('')
  const [loadingPinwheelSession, setLoadingPinwheelSession] = useState(false)
  const [showDisclosureDrawer, setShowDisclosureDrawer] = useState(false)
  const { loading, userId, accountId } = useCheckingFundingData()
  const [isMOCDDWaiverEnabled] = useFeature(mocDDWaiver)
  const {
    startPolling,
    isPolling: isPollingForFundingInitiated,
    loading: loadingFundingInitiated,
  } = useFundingInitiatedPolling(() => {
    isDynamicFunnelEnabled
      ? routeToPage(PageName.Funding, ActionType.Continue)
      : route({ from: FlowRoute.Funding, action: 'continue' })
  })
  const { loading: isCheckingForSoftFail } = useKYCFailRedirect({
    onSoftFail() {
      history.replace(FlowRoute.Institutions)
    },
    /**
     * Hard fail users should technically not make it to this point, they should be stopped
     * on the CardOrderReview step BUT if BE isn't able to resolve their status in time there's an
     * edge case where they may hard fail by the time they reach this step. In this case, we just want to
     * skip them to the end of the flow and have them download the app
     */
    onHardFail() {
      history.replace(FlowRoute.Success)
    },
  })
  const showPinwheel = () => {
    if (!loadingPinwheelSession) {
      track.pinWheelModalOpen()
      setShowDisclosureDrawer(false)
      setLoadingPinwheelSession(true)
      getPinwheelToken({
        variables: {
          userId,
          accountId,
        },
      }).then(() => {
        setLoadingPinwheelSession(false)
      })
    }
  }

  const onDirectDepositClick = () => {
    track.directDepositTapped()
    setShowDisclosureDrawer(true)
  }

  const [getPinwheelToken, { data: createDDLinkData, loading: isLoadingDDLinkData }] = useMutation(
    CreateDDLinkInfo,
  )

  const onBankLinkClick = () => {
    track.bankLinkingTapped()
    isDynamicFunnelEnabled
      ? routeToPage(PageName.Funding, ActionType.Link)
      : route({ from: FlowRoute.Funding, action: 'link' })
  }

  const menuItems = [
    {
      disabled: !!pinwheelToken?.length || isLoadingDDLinkData,
      icon: (
        <AcornsIcon
          icon={AcornsIcon.Icon.TransactionDirectDepositFv}
          height={'24px'}
          width={'24px'}
        />
      ),
      label: getRemoteString({
        key: isMOCDDWaiverEnabled
          ? 'registration.funding.checking.direct_deposit.label'
          : 'registration.funding.checking.direct_deposit.label_non_sup_script',
      }),
      subText: getRemoteString({ key: 'registration.funding.checking.direct_deposit.body' }),
      action: onDirectDepositClick,
    },
    {
      icon: <AcornsIcon icon={AcornsIcon.Icon.UtilityBankFv} height={'24px'} width={'24px'} />,
      label: getRemoteString({ key: 'registration.funding.checking.bank_linking.label' }),
      subText: getRemoteString({ key: 'registration.funding.checking.bank_linking.body' }),
      action: onBankLinkClick,
    },
  ]

  const onPinwheelSuccess = () => {
    track.pinWheelSuccess()
    startPolling()
  }

  useEffect(() => {
    if (createDDLinkData?.createDirectDepositLinkInfo?.results?.token) {
      setPinwheelToken(createDDLinkData?.createDirectDepositLinkInfo?.results?.token)
      setShowPinwheelModal(true)
    }
  }, [createDDLinkData])

  if (loading || isPollingForFundingInitiated || loadingFundingInitiated || isCheckingForSoftFail) {
    return <CenteredSpinner />
  }

  return (
    <>
      <Nav back={isDynamicFunnelEnabled && isBackEnabled(PageName.Funding)} />
      <Layout>
        <Container>
          <Content>
            <Title data-testid={TestIDs.FUNDING_CHECKING.TITLE}>
              {getRemoteString({ key: 'registration.funding.checking.header' })}
            </Title>
            <Text data-testid={TestIDs.FUNDING_CHECKING.TEXT}>
              {getRemoteString({ key: 'registration.funding.checking.body' })}
            </Text>
            <FundingMenu menuItems={menuItems} />
            {isMOCDDWaiverEnabled && (
              <StyledFootnote>
                <StyledRemoteString
                  stringKey="checking.card_order.fund_account.landing.direct_deposit.waiver"
                  variables={['direct deposit']}
                  modifiers={[{ bold: 600, color: themeObj.colors.green }]}
                  wrappers={[
                    {
                      component: Link,
                      props: {
                        href: getRemoteString({ key: 'checking.moc.terms_and_conditions.link' }),
                        target: '_blank',
                        style: { textDecoration: 'none' },
                      },
                    },
                  ]}
                />
              </StyledFootnote>
            )}
          </Content>

          <StyledImage src={FundingSourceImage} />
        </Container>
      </Layout>
      <DisclosureDrawer
        showDisclosureDrawer={showDisclosureDrawer}
        closeDisclosureDrawer={() => setShowDisclosureDrawer(false)}
        disclosureDrawerBackButtonPress={() => setShowDisclosureDrawer(false)}
        goToPinwheel={showPinwheel}
        isLoadingDDLinkData={isLoadingDDLinkData}
      />
      <PinwheelModal
        linkToken={pinwheelToken}
        open={showPinwheelModal}
        onSuccess={onPinwheelSuccess}
        onExit={error => {
          setPinwheelToken('')
          setShowPinwheelModal(false)

          if (error?.code) {
            track.pinwheelErrorToast()
            showToast({
              title: getRemoteString({ key: 'registration.funding.checking.error_toast.title' }),
              body: getRemoteString({ key: 'registration.funding.checking.error_toast.body' }),
            })
          }
        }}
      />
      <Toast />
    </>
  )
})

export default CheckingFunding
